import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import socket from "../socket";
import { useAuth } from "./AuthContext";

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const { user } = useAuth(); 
  const [walletUpdate, setWalletUpdate] = useState(null);
  const [orderUpdates, setOrderUpdates] = useState(null);
  const [isConnected, setIsConnected] = useState(socket.connected);

  // Clear system signals after components have processed the synchronization
  const clearWalletUpdate = useCallback(() => setWalletUpdate(null), []);
  const clearOrderUpdates = useCallback(() => setOrderUpdates(null), []);

  useEffect(() => {
    if (!user?._id) {
      if (socket.connected) socket.disconnect();
      return;
    }

    // MANDATORY: Manually trigger the connection since autoConnect is false
    if (!socket.connected) {
      socket.connect();
    }

    const onConnect = () => {
      setIsConnected(true);
      // Tenant-aware join payload mapping
      const joinPayload = user.role === "admin" 
        ? { role: "admin", adminId: user.adminId || user._id } 
        : String(user._id);

      socket.emit("join", joinPayload); 
      console.log("%c[SOCKET] Joined Tenant Room / Uplink:", "color: #10b981", joinPayload);
    };

    const onDisconnect = () => setIsConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    // If already connected when effect runs, join immediately
    if (socket.connected) onConnect();

    socket.on("walletUpdate", (data) => {
      setWalletUpdate({ ...data, ts: Date.now() });
    });

    socket.on("orderUpdated", (order) => {
      setOrderUpdates({ ...order, ts: Date.now() });
    });

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("walletUpdate");
      socket.off("orderUpdated");
    };
  }, [user?._id, user?.role, user?.adminId]);

  return (
    <SocketContext.Provider 
      value={{ 
        walletUpdate, 
        orderUpdates, 
        clearWalletUpdate, 
        clearOrderUpdates,
        isConnected 
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be utilized within a SocketProvider");
  }
  return context;
};